/* eslint-disable react/jsx-filename-extension */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import TimeMenu from './TimeMenu';

import Required from '../../../icons/Required';
import './TimeInput.less';

import SearchDropdownWithStaticValue from '../../dropdowns/SearchDropdownWithStaticValue/SearchDropdownWithStaticValue';
import {
  forceTime,
  filterTimeOptions,
  options,
  findTimeOption,
} from '../../../../helpers/datetime';
import useKeyTrigger from '../../hooks/useKeyTrigger';
import { isEnter, isTab } from '../../../../helpers/keyboard';

/**
 * Component for selecting a time
 * @param   {function} onSelect
 * @param   {boolean} required
 * @param   {string} label // text Label above input
 * @param   {string} error
 * @param   {string} minTime min selection time in 24:00 format
 * @param   {string} maxTime max selection time in 24:00 format
 * @param   {integer} minuteStep granularity of minute selection
 * @return  {Component}
 */

const TimeInput = ({
  label,
  defaultTime,
  minTime,
  maxTime,
  minuteStep,
  onSelect,
  required,
  error,
  testId,
}) => {
  const [timeLabel, setTimeLabel] = useState(defaultTime);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [show, setShow] = useState(false);
  const [hasError, setError] = useState(false);
  const dispatch = useDispatch();
  const nextButtonDisabled = useSelector((state) => _.get(state, 'profile.nextButtonDisabled'));

  useEffect(() => {
    setTimeLabel(defaultTime);
  }, [defaultTime]);

  const validTimeInput = (text) => /^(?:[0-9]+:[0-9]{2}\s*[ap]m)$/.test(text);

  useEffect(() => {
    if (validTimeInput(timeLabel)) {
      setError(false);
    }
  }, [dispatch, nextButtonDisabled, timeLabel]);

  const closeCurrentMenu = () => {
    setShow(false);
    const topOption = _.first(filteredOptions);
    setTimeLabel(_.get(topOption, 'label', defaultTime));
    onSelect(_.get(topOption, 'id'));
  };

  const { handleKeyTrigger } = useKeyTrigger({
    triggerActions: [
      {
        trigger: isTab,
        replacementAction: closeCurrentMenu,
      },
      {
        trigger: isEnter,
        replacementAction: closeCurrentMenu,
      },
    ],
  });

  const onKeyDown = handleKeyTrigger;

  const timeOptions = options({
    minuteStep: minuteStep || 5,
    minTime,
    maxTime,
  });

  const typedChar = (previousValue, newValue) => {
    for (let i = 0; i < previousValue.length; i++) {
      if (previousValue.charAt(i) !== newValue.charAt(i)) {
        return newValue.charAt(i);
      }
    }
    return _.last(newValue);
  };

  const timeFormat = (e, previousValue = '') => {
    e.preventDefault();
    const regEx = /[0-9:apm]/;
    let newValue = _.get(e, 'target.value');
    const isBackspace = previousValue.length > newValue.length;
    if (isBackspace || typedChar(previousValue, newValue) === ' ') {
      // if backspace, don't format
      const deletedChars = previousValue
        .split('')
        .filter((c) => newValue.split('').indexOf(c) < 0);
      // have to leapfrog the auto-generated characters
      if (
        [' ', ':', 'm'].find((c) => deletedChars.includes(c))
        && deletedChars.length === 1
        && newValue.length > 1
      ) {
        newValue = newValue.slice(0, -1);
      }
      setTimeLabel(newValue);
      return newValue;
    }

    if (regEx.test(typedChar(previousValue, newValue))) {
      newValue = forceTime(newValue);
      setTimeLabel(newValue);
      return newValue;
    } // invalid character

    setTimeLabel(previousValue);
    return previousValue;
  };

  const onFilterChange = (e) => {
    const filter = e.target.value;
    if (filter.slice(-1) === 'm') {
      setShow(false);
      if (onSelect) onSelect(filter);
    } else {
      setShow(true); // keeps open on backspace
    }
  };

  const blur = (e) => {
    const textInput = _.get(e, 'target.value');
    if (!textInput) {
      return;
    }
    if (!validTimeInput(textInput)) {
      setError(true);
    }

    setShow(false);
    const filteredOptionsList = filterTimeOptions(textInput, timeOptions);
    setTimeLabel(
      filteredOptionsList.length
        ? _.first(filteredOptionsList).label
        : textInput,
    );
  };

  const handleSelect = (e) => {
    if (onSelect) onSelect(e);
    setTimeLabel(
      (timeOptions.find((o) => o.id === e) || findTimeOption(e)).label,
    ); // changes the label on selection
  };

  const handleToggleOpen = (o) => {
    setShow(o); // keeps local show in sync
  };

  return (
    // eslint-disable-next-line react/jsx-filename-extension
    <div className="time-input">
      <div className={`time-input-label ${error ? 'error' : ''}`}>
        {label}
        {required && <Required />}
      </div>
      <SearchDropdownWithStaticValue
        label={timeLabel}
        onBlur={blur}
        placeholder="Select Time"
        forceFormat={timeFormat}
        show={show}
        onChange={onFilterChange}
        onKeyDown={onKeyDown}
        onToggleOpen={handleToggleOpen}
        defaultValue={timeLabel}
        error={hasError}
        testId={testId}
      >
        <TimeMenu
          timeOptions={timeOptions}
          onSelect={handleSelect}
          setFilteredOptions={setFilteredOptions}
        />
      </SearchDropdownWithStaticValue>
    </div>
  );
};

export { options };

export default TimeInput;
